import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { AuthImage } from "@/components/AuthImage";
import { PullToRefresh } from "@/components/PullToRefresh";
import { Plus, Eye, Clock, CheckCircle2, FileImage, RefreshCw, AlertTriangle, Navigation, TrendingUp, Receipt, Crown, Lock, Gift, Car, Pencil, Trash2, Fuel, MessageSquare } from "lucide-react";
import VehicleModal from "@/components/VehicleModal";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { toast } from "sonner";

export default function OwnerDashboard() {
  const { user } = useAuth();
  const { referralsEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // issue_id pending delete confirmation
  const [referralNotifyChecked, setReferralNotifyChecked] = useState(false);
  const [referralNotified, setReferralNotified] = useState(false);
  const [loading, setLoading] = useState(true);

  // MY GARAGE state
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [unreadChat, setUnreadChat] = useState({});

  const reload = async () => {
    try {
      const res = await api.get("/issues");
      setIssues(res.data);
    } catch { /* silent - issues will be empty */ } finally { setLoading(false); }
  };

  const loadVehicles = async () => {
    try {
      const res = await api.get("/vehicles");
      setVehicles(res.data);
      // Auto-select if only one vehicle
      if (res.data.length === 1) {
        setSelectedVehicle(res.data[0]);
      }
    } catch { /* silent */ } finally { setVehiclesLoading(false); }
  };

  const loadUnreadChat = async (issueList) => {
    try {
      const ids = issueList.map(i => i.issue_id).join(",");
      if (!ids) return;
      const res = await api.get(`/issues/chat/bulk-unread?issue_ids=${ids}`);
      setUnreadChat(res.data.unread || {});
    } catch { /* silent */ }
  };

  useEffect(() => { reload(); loadVehicles(); }, []);

  useEffect(() => {
    if (issues.length > 0) loadUnreadChat(issues);
  }, [issues]);

  const submitReferralNotify = async () => {
    try {
      await api.post("/referral-notify", {});
    } catch { /* silently ignore */ }
    setReferralNotified(true);
  };

  const deleteIssue = async (issue_id) => {
    try {
      await api.delete(`/issues/${issue_id}`);
      setIssues(prev => prev.filter(i => i.issue_id !== issue_id));
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete issue.");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const repost = async (e, issueId) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/issues/${issueId}/repost`);
      toast.success("Job reposted — back in shop feeds for 7 days.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Repost failed");
    }
  };

  const closeIssue = async (e, issueId) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/issues/${issueId}/close`);
      toast.success("Issue marked as resolved.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to close issue");
    }
  };

  const handleVehicleSave = async (data) => {
    setVehicleSaving(true);
    try {
      if (editVehicle) {
        await api.put(`/vehicles/${editVehicle.vehicle_id}`, data);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", data);
        toast.success("Vehicle added to garage");
      }
      setShowVehicleModal(false);
      setEditVehicle(null);
      loadVehicles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save vehicle");
    } finally {
      setVehicleSaving(false);
    }
  };

  const deleteVehicle = async (e, v) => {
    e.stopPropagation();
    if (!confirm(`Remove ${v.year} ${v.make} ${v.model} from your garage?`)) return;
    try {
      await api.delete(`/vehicles/${v.vehicle_id}`);
      toast.success("Vehicle removed");
      if (selectedVehicle?.vehicle_id === v.vehicle_id) {
        setSelectedVehicle(null);
      }
      loadVehicles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove vehicle");
    }
  };

  const openEdit = (e, v) => {
    e.stopPropagation();
    setEditVehicle(v);
    setShowVehicleModal(true);
  };

  const openAdd = () => {
    setEditVehicle(null);
    setShowVehicleModal(true);
  };

  const handleNewIssue = () => {
    if (!selectedVehicle) return;
    navigate("/post", { state: { vehicle: selectedVehicle } });
  };

  const stats = {
    total: issues.length,
    open: issues.filter((i) => i.status === "open").length,
    in_progress: issues.filter((i) => i.status === "in_progress").length,
    completed: issues.filter((i) => i.status === "completed").length,
  };

  const canPostIssue = !!selectedVehicle;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />

      <PullToRefresh onRefresh={() => { reload(); loadVehicles(); }}>
        <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="owner-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="label-eyebrow mb-3">OWNER DASHBOARD</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Your garage.</h1>
            <p className="text-zinc-400 mt-2">Every issue you post is anonymous until you choose a shop.</p>
          </div>
        </div>

        {/* ═══════════════ MY GARAGE ═══════════════ */}
        <div className="mb-8" data-testid="my-garage-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Car className="w-5 h-5 text-[#FF3B30]" strokeWidth={1.8} />
              <div className="label-eyebrow text-zinc-400">MY GARAGE</div>
            </div>
            <button
              onClick={openAdd}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FF3B30] hover:text-[#FF3B30]/80 transition-colors"
              data-testid="add-vehicle-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add Vehicle
            </button>
          </div>

          {vehiclesLoading ? (
            <div className="border border-white/10 bg-[#121214] p-6 text-sm text-zinc-600">Loading vehicles...</div>
          ) : vehicles.length === 0 ? (
            <div className="border border-dashed border-white/10 p-8 text-center">
              <Car className="w-10 h-10 text-zinc-700 mx-auto mb-4" strokeWidth={1.2} />
              <div className="label-eyebrow mb-3 text-zinc-500">NO VEHICLES YET</div>
              <p className="text-zinc-400 mb-4 text-sm">Add your first vehicle to start posting issues.</p>
              <button
                onClick={openAdd}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="add-first-vehicle-btn"
              >
                <Plus className="w-4 h-4" /> Add Your Vehicle
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {vehicles.map((v) => {
                const isSelected = selectedVehicle?.vehicle_id === v.vehicle_id;
                return (
                  <button
                    key={v.vehicle_id}
                    onClick={() => setSelectedVehicle(isSelected ? null : v)}
                    className={`text-left p-4 border transition-all relative group ${
                      isSelected
                        ? "border-[#FF3B30] bg-[#FF3B30]/5 ring-1 ring-[#FF3B30]/30"
                        : "border-white/10 bg-[#121214] hover:border-white/20 hover:bg-[#121214]/80"
                    }`}
                    data-testid={`garage-vehicle-${v.vehicle_id}`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <div className="w-3 h-3 rounded-full bg-[#FF3B30] animate-pulse" />
                      </div>
                    )}
                    <div className="font-heading text-base font-bold text-white mb-1">
                      {v.year} {v.make} {v.model}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <Fuel className="w-3 h-3" />
                      <span className="capitalize">{v.fuel_type}</span>
                    </div>
                    {/* Edit / Delete actions */}
                    <div className="absolute bottom-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => openEdit(e, v)}
                        className="text-zinc-500 hover:text-white transition-colors p-1"
                        data-testid={`edit-vehicle-${v.vehicle_id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => deleteVehicle(e, v)}
                        className="text-zinc-500 hover:text-[#FF3B30] transition-colors p-1"
                        data-testid={`delete-vehicle-${v.vehicle_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Vehicle selection hint */}
          {vehicles.length > 0 && !selectedVehicle && (
            <div className="mt-3 text-[11px] text-[#FF9500] flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" />
              Select a vehicle above before posting a new issue.
            </div>
          )}
        </div>

        {/* ═══════════════ + NEW ISSUE BUTTON ═══════════════ */}
        <div className="mb-8">
          <button
            onClick={handleNewIssue}
            disabled={!canPostIssue}
            className={`btn-primary flex items-center gap-2 w-fit ${!canPostIssue ? "opacity-40 cursor-not-allowed" : ""}`}
            data-testid="btn-new-issue"
          >
            <Plus className="w-4 h-4" /> New Issue
            {selectedVehicle && (
              <span className="text-[10px] opacity-70 ml-2">
                ({selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model})
              </span>
            )}
          </button>
        </div>

        {/* Bento stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-12">
          {[
            { label: "TOTAL ISSUES", value: stats.total },
            { label: "OPEN", value: stats.open, accent: true },
            { label: "IN PROGRESS", value: stats.in_progress },
            { label: "COMPLETED", value: stats.completed },
          ].map((s) => (
            <div key={s.label} className="bg-[#09090B] p-6">
              <div className="label-eyebrow mb-3">{s.label}</div>
              <div className={`font-heading text-4xl font-black ${s.accent ? "text-[#FF3B30]" : ""}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Mileage Feature Shortcuts */}
        <div className="mb-12">
          <div className="label-eyebrow mb-4 text-zinc-500">DRIVER TOOLS</div>
          <div className="grid grid-cols-3 gap-3">
            <Link
              to="/mileage"
              state={{ tab: "trips" }}
              className="bg-zinc-900 border border-white/10 p-4 flex flex-col items-center gap-3 hover:border-[#FF3B30]/50 hover:bg-zinc-800 transition-colors"
              data-testid="shortcut-auto-track"
            >
              <Navigation className="w-6 h-6 text-[#FF3B30]" strokeWidth={1.8} />
              <span className="label-eyebrow text-xs text-zinc-300">AUTO-TRACK</span>
            </Link>
            <Link
              to="/mileage"
              state={{ tab: "profit_radar" }}
              className="bg-zinc-900 border border-white/10 p-4 flex flex-col items-center gap-3 hover:border-[#FF3B30]/50 hover:bg-zinc-800 transition-colors"
              data-testid="shortcut-profit-radar"
            >
              <TrendingUp className="w-6 h-6 text-[#FF9500]" strokeWidth={1.8} />
              <span className="label-eyebrow text-xs text-zinc-300">PROFIT RADAR</span>
            </Link>
            <Link
              to="/mileage"
              state={{ tab: "receipts" }}
              className="bg-zinc-900 border border-white/10 p-4 flex flex-col items-center gap-3 hover:border-[#FF3B30]/50 hover:bg-zinc-800 transition-colors"
              data-testid="shortcut-receipt-vault"
            >
              <Receipt className="w-6 h-6 text-[#34C759]" strokeWidth={1.8} />
              <span className="label-eyebrow text-xs text-zinc-300">RECEIPT VAULT</span>
            </Link>
          </div>
        </div>


        {/* Refer & Earn Banner — coming soon (hidden unless REFERRALS_ENABLED) */}
        {referralsEnabled && (
        <div className="block mb-6">
          <div className="border border-[#FF9500]/30 bg-gradient-to-r from-[#FF9500]/8 to-transparent p-6 relative overflow-hidden">
            <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#FF9500] text-black">
              Coming Soon
            </span>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#FF9500]/15 flex items-center justify-center shrink-0 mt-0.5">
                <Gift className="w-6 h-6 text-[#FF9500]" strokeWidth={1.8} />
              </div>
              <div className="flex-1 pr-24">
                <div className="label-eyebrow text-[#FF9500] mb-1">REFER & EARN</div>
                <p className="text-zinc-300 text-sm">Earn monthly commission for every car repair shop you refer — for as long as they stay subscribed.</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs px-2 py-0.5 border border-[#FF9500]/40 text-[#FF9500]">Pro · $10/mo</span>
                  <span className="text-xs px-2 py-0.5 border border-[#FF9500]/40 text-[#FF9500]">Premium · $15/mo</span>
                </div>
                <p className="text-zinc-500 text-xs mt-2">Sign up for the waitlist to get full details when we launch.</p>
              </div>
            </div>
            <div className="ml-16">
              {!referralNotified ? (
                !referralNotifyChecked ? (
                  <button
                    onClick={() => setReferralNotifyChecked(true)}
                    className="inline-flex items-center gap-2 border border-[#FF9500]/50 text-[#FF9500] text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#FF9500]/10 transition-colors font-medium"
                  >
                    <Gift className="w-3.5 h-3.5" /> I want to be an Ambassador — Notify Me
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-zinc-300">We'll notify you at your account email when referrals go live. Confirm?</p>
                    <div className="flex gap-2">
                      <button
                        onClick={submitReferralNotify}
                        className="bg-[#FF9500] text-black text-xs uppercase tracking-widest px-4 py-2 font-medium hover:bg-[#FF9500]/90 transition-colors"
                      >
                        Yes, Count Me In
                      </button>
                      <button
                        onClick={() => setReferralNotifyChecked(false)}
                        className="text-zinc-500 text-xs uppercase tracking-widest px-4 py-2 hover:text-zinc-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <div className="text-sm text-[#FF9500] flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> You're an Ambassador! We'll email you when referrals go live.
                </div>
              )}
            </div>
          </div>
        </div>
        )}


        <h2 className="font-heading text-2xl font-bold tracking-tight mb-6">Your Issues</h2>

        {loading ? (
          <div className="text-zinc-500">Loading...</div>
        ) : issues.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center">
            <FileImage className="w-10 h-10 text-zinc-700 mx-auto mb-4" strokeWidth={1.2} />
            <div className="label-eyebrow mb-3 text-zinc-500">NO ISSUES YET</div>
            <p className="text-zinc-400 mb-6">Post your first issue to start receiving private quotes.</p>
            <button
              onClick={handleNewIssue}
              disabled={!canPostIssue}
              className={`btn-primary inline-flex items-center gap-2 ${!canPostIssue ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="empty-new-issue"
            >
              <Plus className="w-4 h-4" /> New Issue
            </button>
            {!canPostIssue && (
              <p className="text-[11px] text-[#FF9500] mt-3">Select a vehicle in your garage first.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {issues.map((i) => {
              const isExpired = i.status === "expired" || i.expired;
              return (
                <Link to={`/issue/${i.issue_id}`} key={i.issue_id} className="card-interactive p-0 overflow-hidden block" data-testid={`issue-card-${i.issue_id}`}>
                  <div className="aspect-video bg-zinc-900 relative">
                    {i.file_paths?.[0] ? (
                      <AuthImage path={i.file_paths[0]} className={`w-full h-full object-cover ${isExpired ? "grayscale opacity-60" : ""}`} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-700">
                        <FileImage className="w-10 h-10" strokeWidth={1.2} />
                      </div>
                    )}
                    <StatusBadge status={isExpired ? "expired" : i.status} />
                    {false && i.is_urgent &&  (
                      <span className="absolute top-3 right-3 text-xs uppercase tracking-widest bg-[#FF3B30] text-white px-2 py-1 flex items-center gap-1.5 animate-pulse" data-testid={`urgent-badge-${i.issue_id}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-white" /> URGENT
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="label-eyebrow text-zinc-500">{i.vehicle_make || "Unknown"} · {i.vehicle_year || "—"}</div>
                      {i.unread_bids_count > 0 && (
                        <span className="inline-flex items-center gap-1 bg-[#FF3B30] text-white text-[10px] font-black px-2 py-0.5 uppercase tracking-widest animate-pulse">
                          <MessageSquare className="w-3 h-3" />
                          {i.unread_bids_count} {i.unread_bids_count === 1 ? "QUOTE" : "QUOTES"}
                        </span>
                      )}
                      {unreadChat[i.issue_id] && (
                        <span className="inline-flex items-center gap-1 bg-[#007AFF] text-white text-[10px] font-black px-2 py-0.5 uppercase tracking-widest">
                          <MessageSquare className="w-3 h-3" />
                          MSG
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-base font-bold mb-2 line-clamp-1">{i.title}</h3>
                    <p className="text-sm text-zinc-400 line-clamp-2">{i.description}</p>
                    {isExpired && (
                      <div className="mt-4 border border-[#FF9500]/40 bg-[#FF9500]/5 p-3 flex items-start gap-2 text-xs">
                        <AlertTriangle className="w-4 h-4 text-[#FF9500] shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-[#FF9500] font-semibold mb-2">Shops can no longer see this</div>
                          <button onClick={(e) => repost(e, i.issue_id)} className="btn-secondary text-xs py-1.5 flex items-center gap-1.5" data-testid={`repost-${i.issue_id}`}>
                            <RefreshCw className="w-3 h-3" /> Repost for 7 more days
                          </button>
                        </div>
                      </div>
                    )}
                    {i.ai_category && !isExpired && (
                      <div className="mt-4 flex gap-2">
                        <span className="text-xs uppercase tracking-widest border border-[#007AFF]/40 text-[#007AFF] px-2 py-1">{i.ai_category}</span>
                        {i.ai_severity && <span className="text-xs uppercase tracking-widest border border-white/10 text-zinc-400 px-2 py-1">{i.ai_severity}</span>}
                      </div>
                    )}
                    {(i.status === "open" || i.status === "in_progress") && !isExpired && (
                      <div className="mt-4">
                        <button
                          onClick={(e) => closeIssue(e, i.issue_id)}
                          className="btn-secondary text-xs py-1.5 flex items-center gap-1.5 w-full justify-center"
                          data-testid={`mark-resolved-${i.issue_id}`}
                        >
                          <CheckCircle2 className="w-3 h-3" /> MARK RESOLVED
                        </button>
                      </div>
                    )}
                    {/* Delete button */}
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteConfirm(i.issue_id); }}
                        className="p-1.5 text-zinc-600 hover:text-[#FF3B30] transition-colors"
                        data-testid={`delete-issue-${i.issue_id}`}
                        title="Delete issue"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
            onClick={() => setDeleteConfirm(null)}
          >
            <div
              className="bg-zinc-900 border border-white/10 p-6 max-w-sm w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-heading text-lg font-bold mb-2">Delete this issue?</h3>
              <p className="text-sm text-zinc-400 mb-6">
                This can't be undone. Any pending quotes will be voided and shops will be notified.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 btn-secondary text-xs py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteIssue(deleteConfirm)}
                  className="flex-1 bg-[#FF3B30] text-white text-xs uppercase tracking-widest py-2 hover:bg-[#FF3B30]/90 transition-colors"
                  data-testid="confirm-delete-issue"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Premium features section */}
        {(() => {
          const isPremium = user?.subscription_tier === "owner_premium";
          const tools = [
            {
              href: "/mileage",
              Icon: Navigation,
              label: "Mile Tracking",
              desc: "Auto-log every drive for tax deductions.",
              color: "text-[#FF3B30]",
              bg: "bg-[#FF3B30]/10",
              freeAccess: true,
            },
            {
              href: "/owner/receipts",
              Icon: Receipt,
              label: "Receipts",
              desc: "Snap and total your car expenses.",
              color: "text-[#34C759]",
              bg: "bg-[#34C759]/10",
            },
            {
              href: "/owner/profit-radar",
              Icon: TrendingUp,
              label: "Profit Radar",
              desc: "Estimate your take-home after every cost.",
              color: "text-[#007AFF]",
              bg: "bg-[#007AFF]/10",
            },
          ];
          return (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-4">
                <div className="label-eyebrow">OWNER PREMIUM TOOLS</div>
                {!isPremium && (
                  <Link to="/owner/upgrade" className="flex items-center gap-1.5 text-xs font-display tracking-widest text-[#FFB800] hover:text-[#FFB800]/80">
                    <Crown className="w-3.5 h-3.5" /> UNLOCK
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {tools.map(({ href, Icon, label, desc, color, bg, freeAccess }) =>
                  isPremium || freeAccess ? (
                    <Link key={label} to={href} className="group border border-white/10 bg-[#121214] p-6 hover:border-white/20 transition-colors">
                      <div className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center mb-3`}>
                        <Icon className={`w-4 h-4 ${color}`} />
                      </div>
                      <div className="font-semibold text-white mb-1">{label}</div>
                      <div className="text-sm text-zinc-500">{desc}</div>
                    </Link>
                  ) : (
                    <Link key={label} to="/owner/upgrade" className="group relative border border-white/5 bg-[#121214]/60 p-6 hover:border-[#FFB800]/20 transition-colors overflow-hidden">
                      <div className="absolute top-3 right-3">
                        <Lock className="w-3.5 h-3.5 text-zinc-600" />
                      </div>
                      <div className={`w-9 h-9 rounded-full ${bg} opacity-30 flex items-center justify-center mb-3`}>
                        <Icon className={`w-4 h-4 ${color}`} />
                      </div>
                      <div className="font-semibold text-zinc-500 mb-1">{label}</div>
                      <div className="text-sm text-zinc-600">{desc}</div>
                      <div className="mt-3 text-[10px] font-display tracking-widest text-[#FFB800]/60">PREMIUM</div>
                    </Link>
                  )
                )}
              </div>
              {!isPremium && (
                <Link to="/owner/upgrade" className="mt-4 flex items-center justify-center gap-2 w-full py-3 border border-[#FFB800]/30 text-[#FFB800] text-xs font-display tracking-widest hover:bg-[#FFB800]/5 transition-colors">
                  <Crown className="w-3.5 h-3.5" /> UNLOCK OWNER PREMIUM — $3.99/MO
                </Link>
              )}
            </div>
          );
        })()}
        </main>
      </PullToRefresh>

      {/* Vehicle Modal (Add/Edit) */}
      <VehicleModal
        open={showVehicleModal}
        onClose={() => { setShowVehicleModal(false); setEditVehicle(null); }}
        onSave={handleVehicleSave}
        vehicle={editVehicle}
        saving={vehicleSaving}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: { label: "OPEN", c: "bg-[#FF3B30] text-white", icon: Eye },
    in_progress: { label: "IN PROGRESS", c: "bg-[#FF9500] text-black", icon: Clock },
    completed: { label: "COMPLETED", c: "bg-[#34C759] text-black", icon: CheckCircle2 },
    closed: { label: "CLOSED", c: "bg-zinc-700 text-white", icon: CheckCircle2 },
    expired: { label: "EXPIRED", c: "bg-zinc-700 text-zinc-300", icon: AlertTriangle },
  };
  const x = map[status] || map.open;
  const Icon = x.icon;
  return (
    <span className={`absolute top-3 left-3 ${x.c} text-xs font-bold uppercase tracking-widest px-2 py-1 flex items-center gap-1.5`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} /> {x.label}
    </span>
  );
}
